import { FunctionComponent, ReactElement, useCallback, useState } from "react";

import {
    Button,
    Chip,
    IconButton,
    Snackbar as MuiSnackbar,
    SnackbarCloseReason,
    Typography,
    styled,
} from "@mui/material";

import { Add, Close, FilterList } from "@mui/icons-material";

import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridRowParams,
    GridRowSelectionModel,
    GridValueGetterParams,
} from "@mui/x-data-grid";
import { formatDistance } from "date-fns";
import {
    InstantSearch,
    Pagination,
    useHits,
    useInstantSearch,
} from "react-instantsearch";
import { Link, useNavigate } from "react-router-dom";

import { EmptyRecords, StatusChip } from "../../components/common";
import { SearchBox } from "../../components/common/typesense";
import { useDeleteFreebieRule } from "../../hooks";
import {
    FreebieRuleStatus,
    FreebieRuleType,
    FreebieRuleTypeNames,
    freebieRuleStatusNames,
} from "../../utils/enums";

import { FilterDialog } from "./FilterDialog";
import { typesenseAdapter } from "./config/typesense.config.adapter";

const Root = styled("main")`
    padding: ${({ theme }) => theme.spacing(3)};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(3)};
`;

const TitleContainer = styled("div")`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const SearchContainer = styled("div")`
    display: flex;
    gap: ${({ theme }) => theme.spacing(2)};
    align-items: center;
`;

const Title = styled(Typography)`
    font-size: 24px;
    font-weight: 500;
`;

const EmptyRecordsContainer = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
    align-items: center;
`;

const Snackbar = styled(MuiSnackbar)`
    & .MuiPaper-root {
        background-color: ${({ theme }) => theme.palette.common.white};
        color: ${({ theme }) => theme.palette.common.black};
    }
`;

const StyledDataGrid = styled(DataGrid)`
    .MuiDataGrid-cell:focus {
        outline: none;
    }
    .MuiDataGrid-row:hover {
        cursor: pointer;
    }
`;

const StyledPaginationContainer = styled("div")`
    .ais-Pagination-list {
        display: flex;
        justify-content: center;
        padding: ${({ theme }) => theme.spacing(2)};
        list-style: none;
    }
    .ais-Pagination-item {
        margin: ${({ theme }) => theme.spacing(0, 1)};
    }
    .ais-Pagination-link {
        padding: ${({ theme }) => theme.spacing(1, 2)};
        border: 1px solid ${({ theme }) => theme.palette.divider};
        border-radius: 4px;
        color: ${({ theme }) => theme.palette.text.primary};
        text-decoration: none;
        cursor: pointer;
        transition: background-color 0.2s ease;
        &:hover {
            background-color: ${({ theme }) => theme.palette.action.hover};
        }
    }
    .ais-Pagination-item--disabled .ais-Pagination-link {
        color: ${({ theme }) => theme.palette.action.disabled};
        cursor: not-allowed;
    }
    .ais-Pagination-item--selected .ais-Pagination-link {
        background-color: #042442;
        color: white;
        cursor: default;
    }
`;

const columns: GridColDef[] = [
    {
        field: "name",
        headerName: "Name",
        flex: 0.4,
        minWidth: 250,
    },
    {
        field: "type",
        headerName: "Type",
        flex: 0.2,
        minWidth: 150,
        valueGetter: (params: GridValueGetterParams) =>
            FreebieRuleTypeNames[params.row.type as FreebieRuleType],
        renderCell: (params: GridRenderCellParams<any, string>) => (
            <Chip label={params.value} variant="outlined" />
        ),
    },
    {
        field: "status",
        headerName: "Status",
        flex: 0.2,
        minWidth: 140,
        maxWidth: 150,
        renderCell: (params: GridRenderCellParams) => (
            <StatusChip
                label={
                    freebieRuleStatusNames[
                        params.row.status as FreebieRuleStatus
                    ]
                }
                value={params.row.status}
            />
        ),
    },
    {
        field: "quantity",
        headerName: "Qty",
        flex: 0.1,
        minWidth: 80,
    },
    {
        field: "createdByName",
        headerName: "Created by",
        flex: 0.2,
        minWidth: 150,
        valueGetter: (params: GridValueGetterParams) =>
            params.row.createdByName || "NA",
    },
    {
        field: "createdAt",
        headerName: "Created",
        flex: 0.2,
        minWidth: 150,
        valueGetter: (params: GridValueGetterParams) =>
            `${formatDistance(
                new Date(params.row.createdAt * 1000),
                new Date(),
            )} ago`,
    },
];

const FreebieRulesGrid: FunctionComponent = (): ReactElement => {
    const navigate = useNavigate();
    const { hits, results } = useHits();
    const { refresh } = useInstantSearch();
    const [selectedFreebieRuleIds, setSelectedFreebieRuleIds] = useState<
        string[]
    >([]);
    const deleteFreebieRule = useDeleteFreebieRule("freebieRule");

    const handleRowClick = useCallback(
        (params: GridRowParams) => {
            navigate(`/freebie-rules/${params.row.id}`);
        },
        [navigate],
    );

    const handleCloseSnackbar = useCallback(
        (
            _event: Event | React.SyntheticEvent<any, Event>,
            reason: SnackbarCloseReason,
        ) => {
            if (reason !== "clickaway") {
                setSelectedFreebieRuleIds([]);
            }
        },
        [],
    );

    const handleClose = useCallback(() => {
        setSelectedFreebieRuleIds([]);
    }, []);

    const handleArchiveFreebieRule = useCallback(() => {
        Promise.all(
            selectedFreebieRuleIds.map((freebieRuleId) =>
                deleteFreebieRule.mutateAsync({ id: freebieRuleId }),
            ),
        ).then(() => {
            /*
             * The backend's Typesense delete runs fire-and-forget right
             * after the DB archive write, so give it a moment to land
             * before asking InstantSearch to refetch.
             */
            setTimeout(refresh, 500);
        });
        setSelectedFreebieRuleIds([]);
    }, [deleteFreebieRule, refresh, selectedFreebieRuleIds]);

    if (results && results.nbHits === 0) {
        return (
            <EmptyRecords>
                <EmptyRecordsContainer>
                    <Title>Manage freebie rules</Title>
                    <div>
                        Create eligibility, checkpoint, or discount-code
                        freebie rules that apply automatically in the cart.
                    </div>
                    <Link to="/freebie-rules/new">
                        <Button variant="contained">New Rule</Button>
                    </Link>
                </EmptyRecordsContainer>
            </EmptyRecords>
        );
    }

    return (
        <>
            <StyledDataGrid
                autoHeight={true}
                rows={hits}
                columns={columns}
                disableRowSelectionOnClick={true}
                onRowClick={handleRowClick}
                checkboxSelection={true}
                rowSelectionModel={selectedFreebieRuleIds}
                onRowSelectionModelChange={(
                    newSelection: GridRowSelectionModel,
                ) => {
                    setSelectedFreebieRuleIds(newSelection as string[]);
                }}
            />
            <StyledPaginationContainer>
                <Pagination />
            </StyledPaginationContainer>
            <Snackbar
                open={selectedFreebieRuleIds.length > 0}
                autoHideDuration={null}
                onClose={handleCloseSnackbar}
                message={`${selectedFreebieRuleIds.length} rule${
                    selectedFreebieRuleIds.length > 1 ? "s" : ""
                } selected`}
                ClickAwayListenerProps={{
                    mouseEvent: false,
                    touchEvent: false,
                }}
                action={
                    <>
                        <Button onClick={handleArchiveFreebieRule}>
                            Archive
                        </Button>
                        <IconButton onClick={handleClose}>
                            <Close />
                        </IconButton>
                    </>
                }
            />
        </>
    );
};

export const FreebieRulesScreen: FunctionComponent = (): ReactElement => {
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);

    return (
        <InstantSearch
            searchClient={typesenseAdapter.searchClient}
            indexName={`${process.env.REACT_APP_TYPESENSE_PREFIX}freebie-rules`}
            future={{ preserveSharedStateOnUnmount: true }}
        >
            <Root>
                <TitleContainer>
                    <Title>Freebie Rules</Title>
                    <SearchContainer>
                        <SearchBox />
                        <Button
                            variant="outlined"
                            startIcon={<FilterList />}
                            onClick={() => setFilterDialogOpen(true)}>
                            Filter
                        </Button>
                        <Link to="/freebie-rules/new">
                            <Button
                                variant="contained"
                                startIcon={<Add fontSize="small" />}>
                                New Rule
                            </Button>
                        </Link>
                    </SearchContainer>
                </TitleContainer>
                <FreebieRulesGrid />
                <FilterDialog
                    open={filterDialogOpen}
                    onClose={() => setFilterDialogOpen(false)}
                />
            </Root>
        </InstantSearch>
    );
};
